import { useEffect, useRef, useState } from 'react';
import { useDesktopAuth } from './desktop-auth';
import { openWebExternal } from './electron-open-web';

// Custom user button for the Electron desktop session (no Clerk in the renderer).
// Mirrors Clerk's prebuilt UserButton: avatar trigger + dropdown with profile
// header, manage-account and sign-out. Account management delegates to the web.
export function ElectronUserButton() {
  const { userName, userEmail, userImageUrl, signOut } = useDesktopAuth();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const name = userName ?? userEmail ?? 'Usuario';
  const initial = name.charAt(0).toUpperCase();

  // ESC closes; focus returns to the trigger so keyboard users aren't stranded.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  function close() {
    setOpen(false);
    triggerRef.current?.focus();
  }

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={`Cuenta de ${name}`}
        aria-haspopup="menu"
        aria-expanded={open}
        title={name}
        className="w-9 h-9 rounded-full bg-accent/10 text-accent text-sm font-semibold flex items-center justify-center overflow-hidden ring-1 ring-stroke hover:ring-accent/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent transition-all"
      >
        {userImageUrl ? (
          <img src={userImageUrl} alt="" className="w-full h-full object-cover" />
        ) : (
          initial
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} aria-hidden="true" />
          <div
            role="menu"
            aria-label="Menú de cuenta"
            className="absolute left-full ml-2 bottom-0 z-50 w-64 rounded-2xl border border-stroke-hi bg-surface-card shadow-xl p-1.5"
          >
            <div className="flex items-center gap-3 px-2.5 py-2.5">
              <span className="w-10 h-10 rounded-full bg-accent/10 text-accent text-base font-semibold flex items-center justify-center overflow-hidden flex-none">
                {userImageUrl ? (
                  <img src={userImageUrl} alt="" className="w-full h-full object-cover" />
                ) : (
                  initial
                )}
              </span>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-ink truncate leading-tight">{name}</p>
                {userEmail && <p className="text-xs text-ink-soft truncate mt-0.5">{userEmail}</p>}
              </div>
            </div>

            <div className="my-1 border-t border-stroke" />

            <button
              role="menuitem"
              onClick={() => { openWebExternal(); close(); }}
              className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-xl text-sm text-ink hover:bg-surface-hover focus-visible:outline-none focus-visible:bg-surface-hover transition-colors"
            >
              <GearIcon />
              Administrar cuenta
            </button>
            <button
              role="menuitem"
              onClick={() => { void signOut(); close(); }}
              className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-xl text-sm text-rose hover:bg-surface-hover focus-visible:outline-none focus-visible:bg-surface-hover transition-colors"
            >
              <SignOutIcon />
              Cerrar sesión
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function GearIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 flex-none text-ink-muted" aria-hidden="true">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}

function SignOutIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 flex-none" aria-hidden="true">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  );
}
